import Model from 'src/app/core/models/model';

/**
 * @model BusinessTypeModel
 * @description
 * Represents the specific "Type" of a business entity within the AquaCloud application.
 * While `BusinessCategory` defines the broad classification (e.g., Farm, Provider),
 * `BusinessType` provides a more granular definition (e.g., Shrimp Farm, Fish Farm, Feed Distributor).
 *
 * **Purpose:**
 * This model serves as a sub-classification entity. It allows the system to apply specific
 * operational workflows, data collection forms, and reporting structures based on the exact
 * nature of the business.
 *
 * **Key Responsibilities:**
 * 1. **Granular Classification**: Distinguishes between specific variations of a business category.
 * 2. **Hierarchy**: Links to a parent `BusinessCategory` via `businessCategoryID`.
 * 3. **UI Logic**: Used to drive dynamic form fields (e.g., a Shrimp Farm needs different fields than a Fish Farm).
 *
 * **Data Flow:**
 * - **Definition**: Master data defined by system administrators.
 * - **Retrieval**: Fetched via `BusinessTypeService`, often filtered by a selected `BusinessCategory`.
 * - **Usage**:
 *   - **Registration**: When a user selects "Farm", they are then asked to select the "Type" (Shrimp/Fish).
 *   - **Profile**: Stored in `BusinessEntity` to determine available features.
 *
 * **User Roles & Permissions:**
 * - **System Admin**: Manages the list of available business types.
 * - **User**: Selects a type during onboarding.
 *
 * **Features:**
 * - Extends `Model` for standard database fields.
 * - Contains foreign key reference to `BusinessCategory`.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class BusinessTypeModel extends Model {
  /**
   * @type {string}
   * @description
   * The human-readable display name of the business type.
   * @example "Shrimp Farm", "Feed Mill"
   */
  name: string;

  /**
   * @type {string}
   * @description
   * A unique system code used for logic branching and identification.
   * Immutable reference for the type.
   * @example "SHRIMP_FARM"
   */
  code: string;

  /**
   * @type {string}
   * @description
   * A brief description explaining what this business type entails.
   * Useful for tooltips or help text during registration.
   */
  description: string;

  /**
   * @type {string}
   * @description
   * The unique identifier of the parent `BusinessCategory` this type belongs to.
   * Acts as a Foreign Key.
   */
  businessCategoryID: string;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the BusinessTypeModel.
   * Calls the parent `Model` constructor.
   */
  constructor() {
    super();
  }
}
